import axios from "axios";
import {addAuthKey} from '@/helpers/addAuthKeyToUrl';

const api_url = `${process.env.VUE_APP_BASE_URL || '/'}index.php`;

function normalizePostCategoriesColumnsResponse(json) {
    if (json.errors || !Array.isArray(json.items)) {
        return { ...json, items: Array.isArray(json.items) ? json.items : [] };
    }

    return json;
}

function normalizePostCategoriesIndexResponse(json) {
    if (json.errors) {
        return { ...json, items: [] };
    }

    if (json.items === undefined || json.items === null) {
        return { ...json, items: [] };
    }

    return json;
}

function normalizePostCategoriesUserColumnsResponse(json) {
    const additional = json.additional || { groupByCategory: 0 };

    if (json.errors) {
        return {
            ...json,
            columns: Array.isArray(json.columns) ? json.columns : [],
            additional,
        };
    }

    return {
        ...json,
        columns: Array.isArray(json.columns) ? json.columns : [],
        additional,
    };
}

export default {
    namespaced: true,

    state: {
        postGroups: [],
        productionGroups: [],
        workloadTypes: [],
        units: [],
        categories: [],
        orderTypes: [],
        places: [],
        additionalWorkloadTypes: [],
        columnsTableForGroup: [],
        categoriesTableForGroup: [],
        userColumnsTableForGroup: [],
        materialValues: [],
        surchargeTypes: [],
        dishes: [],
        recruitmentTemplates: [],
        routeTypes: [],
        deliverySurchargePeriods: [],
        deliverySurchargeTypes: [],
        deliverySurchargeCategories: [],
        deliverySurchargeCriteria: [],
        positionsList: [],
    },

    mutations: {
        setPostGroups(state, postGroups) {
            state.postGroups = postGroups;
        },
        setProductionGroups(state, productionGroups) {
            state.productionGroups = productionGroups;
        },
        setWorkloadTypes(state, workloadTypes) {
            state.workloadTypes = workloadTypes;
        },
        setUnits(state, units) {
            state.units = units;
        },
        setCategories(state, categories) {
            state.categories = categories;
        },
        setOrderTypes(state, orderTypes) {
            state.orderTypes = orderTypes;
        },
        setPlaces(state, places) {
            state.places = places;
        },
        setAdditionalWorkloadTypes(state, additionalWorkloadTypes) {
            state.additionalWorkloadTypes = additionalWorkloadTypes;
        },
        setColumnsTableForGroup(state, columnsTableForGroup) {
            state.columnsTableForGroup = columnsTableForGroup;
        },
        setCategoriesTableForGroup(state, categoriesTableForGroup) {
            state.categoriesTableForGroup = categoriesTableForGroup;
        },
        setUserColumnsTableForGroup(state, userColumnsTableForGroup) {
            state.userColumnsTableForGroup = userColumnsTableForGroup;
        },
        setMaterialValues(state, materialValues) {
            state.materialValues = materialValues;
        },
        setSurchargeTypes(state, surchargeTypes) {
            state.surchargeTypes = surchargeTypes;
        },
        setDishes(state, dishes) {
            state.dishes = dishes;
        },
        setRecruitmentTemplates(state, templates) {
            state.recruitmentTemplates = templates;
        },
        setRouteTypes(state, routeTypes) {
            state.routeTypes = Object.entries(routeTypes).map(([value, label]) => ({ value, label }));
        },

        setDeliverySurchargePeriods(state, items) {
            state.deliverySurchargePeriods = Object.entries(items).map(([value, label]) => ({ value, label }));
        },
        setDeliverySurchargeTypes(state, items) {
            state.deliverySurchargeTypes = items;
        },
        setDeliverySurchargeCategories(state, items) {
            // Убрать пункт Все
            state.deliverySurchargeCategories = items.filter((item) => item.id !== 1);
        },
        setDeliverySurchargeCriteria(state, items) {
            state.deliverySurchargeCriteria = items;
        },
        setPositionsList(state, list) {
            state.positionsList = list;
        },
    },

    getters: {
        getPostGroups(state) {
            return state.postGroups;
        },
        getProductionGroups(state) {
            return state.productionGroups;
        },
        getWorkloadTypes(state) {
            return state.workloadTypes;
        },
        getUnits(state) {
            return state.units;
        },
        getCategories(state) {
            return state.categories;
        },
        getOrderTypes(state) {
            return state.orderTypes;
        },
        getPlaces(state) {
            return state.places;
        },
        getAdditionalWorkloadTypes(state) {
            return state.additionalWorkloadTypes;
        },
        getColumnsTableForGroup(state) {
            return state.columnsTableForGroup;
        },
        getCategoriesTableForGroup(state) {
            return state.categoriesTableForGroup;
        },
        getUserColumnsTableForGroup(state) {
            return state.userColumnsTableForGroup;
        },
        getMaterialValues(state) {
            return state.materialValues;
        },
        getSurchargeTypes(state) {
            return state.surchargeTypes;
        },
        getDishes(state) {
            return state.dishes;
        },
        getRecruitmentTemplates(state) {
            return state.recruitmentTemplates;
        },
        getRouteTypes(state) {
            return state.routeTypes;
        },
        getDeliverySurchargePeriods(state) {
            return state.deliverySurchargePeriods;
        },
        getDeliverySurchargeTypes(state) {
            return state.deliverySurchargeTypes;
        },
        getDeliverySurchargeCategories(state) {
            return state.deliverySurchargeCategories;
        },
        getDeliverySurchargeCriteria(state) {
            return state.deliverySurchargeCriteria;
        },
        getPositionsList(state) {
            return state.positionsList;
        },
    },

    actions: {
        async getTypeLoadReference(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/type-load/index`, token));
            return res.data;
        },
        async getFilialReference(ctx, type) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/filial/index&acc&type=${type}`, token));
            return res.data;
        },

        async getPostGroups(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/type-post/setup-data`, token));
            ctx.commit("setPostGroups", res.data);
        },
        async getProductionGroups(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post/type-post`, token));
            ctx.commit("setProductionGroups", res.data);
        },
        async getWorkloadTypes(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post/type-load`, token));
            ctx.commit("setWorkloadTypes", res.data);
        },
        async getUnits(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post/measure`, token));
            ctx.commit("setUnits", res.data);
            return res.data;
        },
        async getCategories(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post/categories`, token));
            ctx.commit("setCategories", res.data);
        },
        async getOrderTypes(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post/type-order`, token));
            ctx.commit("setOrderTypes", res.data);
        },
        async getPlaces(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post/places`, token));
            ctx.commit("setPlaces", res.data);
        },
        async getAdditionalWorkloadTypes(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post/workload-type`, token));
            ctx.commit("setAdditionalWorkloadTypes", res.data);
        },
        async getMaterialValues(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post-uniform/index`, token));
            ctx.commit("setMaterialValues", res.data);
        },
        async getSurchargeTypes(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post-surcharge/order-types`, token));
            ctx.commit("setSurchargeTypes", res.data);
        },
        async getDishes(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post-surcharge/dishes`, token));
            ctx.commit("setDishes", res.data);
        },
        async getRecruitmentTemplates(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post-recruiting/templates`, token));
            ctx.commit("setRecruitmentTemplates", res.data);
        },
        async getRouteTypes(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/delivery-post/route-type`, token));
            ctx.commit("setRouteTypes", res.data);
        },
        async getDeliverySurchargePeriods(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/delivery-post-surcharge/time-period`, token));
            ctx.commit("setDeliverySurchargePeriods", res.data);
        },
        async getDeliverySurchargeTypes(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/delivery-post-surcharge/type`, token));
            ctx.commit("setDeliverySurchargeTypes", res.data);
        },
        async getDeliverySurchargeCategories(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(
                addAuthKey(`${api_url}/?r=api/delivery-post-surcharge/menu-category`, token),
            );
            ctx.commit("setDeliverySurchargeCategories", res.data);
        },
        async getDeliverySurchargeCriteria(ctx) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/delivery-post-surcharge/criteria`, token));
            ctx.commit("setDeliverySurchargeCriteria", res.data);
        },

        async createGroup(ctx, name) {
            const token = ctx.rootState.authToken;

            const form_data = new FormData();

            form_data.append("name", name);

            const res = await fetch(addAuthKey(`${api_url}/?r=api/type-post/create`, token), {
                method: "post",
                body: form_data,
            });

            const json = await res.json();

            return json;
        },

        async createCategory(ctx, params) {
            const token = ctx.rootState.authToken;
            const codeCategory = params[0];
            const nameCategory = params[1];

            const form_data = new FormData();

            form_data.append("post_type_id", codeCategory);
            form_data.append("name", nameCategory);

            const res = await fetch(addAuthKey(`${api_url}/?r=api/post-categories/create`, token), {
                method: "post",
                body: form_data,
            });

            const json = await res.json();

            return json;
        },

        async renameGroup(ctx, params) {
            const token = ctx.rootState.authToken;
            const groupCode = params[0];
            const groupName = params[1];

            const form_data = new FormData();

            form_data.append("name", groupName);

            const res = await fetch(addAuthKey(`${api_url}/?r=api/type-post/update&code=${groupCode}`, token), {
                method: "put",
                body: form_data,
            });

            const json = await res.json();

            return json;
        },

        async editCategory(ctx, params) {
            const token = ctx.rootState.authToken;
            const categoryId = params[0];
            const categoryName = params[1];
            const groupCode = params[2];

            const form_data = new FormData();

            form_data.append("name", categoryName);
            form_data.append("post_type_id", groupCode);

            const res = await fetch(addAuthKey(`${api_url}/?r=api/post-categories/update&id=${categoryId}`, token), {
                method: "put",
                body: form_data,
            });

            const json = await res.json();

            return json;
        },

        async removeGroup(ctx, code) {
            const token = ctx.rootState.authToken;

            const form_data = new FormData();

            form_data.append("code", code);

            const res = await fetch(addAuthKey(`${api_url}/?r=api/type-post/delete&code=${code}`, token), {
                method: "delete",
                body: form_data,
            });

            let json;

            if (res.status === 204) {
                json = "Группа успешно удалена";
            } else {
                json = await res.json();
            }

            return json;
        },

        async removeCategory(ctx, code) {
            const token = ctx.rootState.authToken;

            const form_data = new FormData();

            form_data.append("code", code);

            const res = await fetch(addAuthKey(`${api_url}/?r=api/post-categories/delete&id=${code}`, token), {
                method: "delete",
                body: form_data,
            });

            let json;

            if (res.status === 204) {
                json = "Группа успешно удалена";
            } else {
                json = await res.json();
            }

            return json;
        },

        async removePost(ctx, code) {
            const token = ctx.rootState.authToken;

            const form_data = new FormData();

            form_data.append("code", code);

            const res = await fetch(addAuthKey(`${api_url}/?r=api/posts/delete&code=${code}`, token), {
                method: "delete",
                body: form_data,
            });

            let json;

            if (res.status === 204) {
                json = "Группа успешно удалена";
            } else {
                json = await res.json();
            }

            return json;
        },

        async moveGroup(ctx, groups) {
            const token = ctx.rootState.authToken;

            const form_data = new FormData();

            groups.forEach((group, index) => {
                form_data.append(`typePosts[${index}]`, group.code);
            });

            const res = await fetch(addAuthKey(`${api_url}/?r=api/type-post/change-order`, token), {
                method: "post",
                body: form_data,
            });

            const json = await res.json();

            return json;
        },

        async moveCategoryToOtherGroup(ctx, params) {
            const token = ctx.rootState.authToken;
            const codeGroup = params[0];
            const idCategory = params[1];

            const form_data = new FormData();

            form_data.append("to", codeGroup);

            const res = await fetch(addAuthKey(`${api_url}/?r=api/post-categories/move&id=${idCategory}`, token), {
                method: "post",
                body: form_data,
            });

            const json = await res.json();

            return json;
        },

        async moveCategoryInCurrentGroup(ctx, params) {
            const token = ctx.rootState.authToken;
            const codeGroup = params[0];
            const categories = params[1];

            const form_data = new FormData();

            categories.forEach((category, index) => {
                form_data.append(`categories[${index}]`, category.id);
            });

            const res = await fetch(
                addAuthKey(`${api_url}/?r=api/post-categories/change-order&typePostId=${codeGroup}`, token),
                {
                    method: "post",
                    body: form_data,
                },
            );

            const json = await res.json();

            return json;
        },

        async movePostToOtherCategory(ctx, params) {
            const token = ctx.rootState.authToken;
            const codeGroup = params[0];
            const idCategory = params[1];
            const codePost = params[2];

            const form_data = new FormData();

            form_data.append("to[post_type]", codeGroup);
            form_data.append("to[category]", idCategory);

            const res = await fetch(addAuthKey(`${api_url}/?r=api/posts/move&code=${codePost}`, token), {
                method: "post",
                body: form_data,
            });

            const json = await res.json();

            return json;
        },

        async movePostInCurrentCategory(ctx, params) {
            const token = ctx.rootState.authToken;
            const groupCode = params[0];
            const categoryId = params[1].id;
            const { posts } = params[1];

            const form_data = new FormData();

            posts.forEach((post, index) => {
                form_data.append(`posts[${index}]`, post.code);
            });

            const res = await fetch(
                addAuthKey(`${api_url}/?r=api/posts/change-order&postTypeId=${groupCode}&categoryId=${categoryId}`, token),
                {
                    method: "post",
                    body: form_data,
                },
            );

            const json = await res.json();

            return json;
        },

        async getColumnsTableForGroup(ctx, groupId) {
            const token = ctx.rootState.authToken;

            const res = await fetch(
                addAuthKey(`${api_url}/?r=api/post-categories/columns&typePostId=${groupId}`, token),
            );

            const json = normalizePostCategoriesColumnsResponse(await res.json());

            ctx.commit("setColumnsTableForGroup", json);

            return json;
        },

        async getCategoriesTableForGroup(ctx, params) {
            const token = ctx.rootState.authToken;
            const { groupId, filtersLocal, filtersGlobal, selectedPositionStatus } = params;

            let isArchive;

            switch (selectedPositionStatus) {
                case 'archived': isArchive = 1; break;
                case 'active': isArchive = 0; break;
            }

            const baseUrl = window.location.origin;
            const queryUrl = new URL(
                addAuthKey(`${baseUrl}${api_url}/?r=api/post-categories/index&typePostId=${groupId}${typeof isArchive === 'number' ? '&is_archive=' + isArchive : ''}`, token),
            );

            filtersLocal.forEach((filter) => {
                if (filter.type === "list") {
                    const values = filter.value.map((item) => item.id);
                    queryUrl.searchParams.append(`filters[${filter.id}][search_type]=`, filter.searchType);
                    values.forEach((id, ind) => {
                        queryUrl.searchParams.append(`filters[${filter.id}][values][${ind}]=`, id);
                    });
                } else if (filter.type === "number" && filter.searchType === "4") {
                    queryUrl.searchParams.append(`filters[${filter.id}][search_type]=`, filter.searchType);
                    queryUrl.searchParams.append(`filters[${filter.id}][values][0]=`, filter.value.start);
                    queryUrl.searchParams.append(`filters[${filter.id}][values][1]=`, filter.value.end);
                } else if (filter.type === "boolean") {
                    if (filter.value === "1")
                        queryUrl.searchParams.append(`filters[${filter.id}][value]=`, filter.value);
                } else {
                    queryUrl.searchParams.append(`filters[${filter.id}][search_type]=`, filter.searchType);
                    queryUrl.searchParams.append(`filters[${filter.id}][value]=`, filter.value);
                }
            });

            filtersGlobal.forEach((filter) => {
                if (filter.id === "search[is_vacant]") {
                    if (filter.value === "1") queryUrl.searchParams.append(`search[is_vacant]`, filter.value);
                } else {
                    const filterId = filter.id.split("_")[0];

                    if (filter.type === "list") {
                        const values = filter.value.map((item) => item.id);
                        queryUrl.searchParams.append(
                            "search[except]=",
                            filter.searchType === "2" || filter.searchType ? "1" : "0",
                        );
                        values.forEach((id, ind) => {
                            queryUrl.searchParams.append(`search[${filterId}][${ind}]=`, id);
                        });
                    } else if (filter.type === "number" && filter.searchType === "4") {
                        queryUrl.searchParams.append(`search[${filterId}][search_type]=`, filter.searchType);
                        queryUrl.searchParams.append(`search[${filterId}][values][0]=`, filter.value.start);
                        queryUrl.searchParams.append(`search[${filterId}][values][1]=`, filter.value.end);
                    } else if (filter.type === "boolean") {
                        if (filter.value === "1")
                            queryUrl.searchParams.append(`filters[${filterId}][value]=`, filter.value);
                    } else {
                        queryUrl.searchParams.append(`search[${filterId}]=`, filter.value);
                    }
                }
            });

            const res = await fetch(queryUrl.toString());

            const json = normalizePostCategoriesIndexResponse(await res.json());

            ctx.commit("setCategoriesTableForGroup", json);

            return json;
        },

        async getUserColumnsTableForGroup(ctx, groupId) {
            const token = ctx.rootState.authToken;

            const res = await fetch(
                addAuthKey(`${api_url}/?r=api/post-categories/user-columns&typePostId=${groupId}`, token),
            );

            const json = normalizePostCategoriesUserColumnsResponse(await res.json());

            ctx.commit("setUserColumnsTableForGroup", json);

            return json;
        },

        async saveUserColumns(ctx, params) {
            const token = ctx.rootState.authToken;
            const groupCode = params[0];
            const columns = params[1];
            const withGrouped = params[2];

            const form_data = new FormData();

            columns.forEach((column, index) => {
                form_data.append(`columns[${index}]`, column);
            });

            form_data.append("additionalSettings[groupByCategory]", withGrouped ? 1 : 0);

            const res = await fetch(
                addAuthKey(`${api_url}/?r=api/post-categories/save-user-columns&typePostId=${groupCode}`, token),
                {
                    method: "post",
                    body: form_data,
                },
            );

            const json = await res.json();

            return json;
        },

        async getPositionsStats(ctx) {
            const token = ctx.rootState.authToken;
            const res = await fetch(addAuthKey(`${api_url}/?r=api/posts/stats`, token));
            const json = await res.json();
            return json;
        },

        async createNewPosition(ctx, positionData) {
            const token = ctx.rootState.authToken;
            const res = await axios.post(addAuthKey(`${api_url}/?r=api/post/create`, token), positionData, {
                validateStatus: (status) => status < 500,
            });

            return res.data;
        },
        async createNewDeliveryPosition(ctx, positionData) {
            const token = ctx.rootState.authToken;
            const res = await axios.post(addAuthKey(`${api_url}/?r=api/delivery-post/create`, token), positionData, {
                validateStatus: (status) => status < 500,
            });
            return res.data;
        },

        async getPositionData(ctx, params) {
            const { position, isDelivery } = params;
            const token = ctx.rootState.authToken;

            if (isDelivery) {
                const res = await axios.get(
                    addAuthKey(`${api_url}/?r=api/delivery-post/view&id=${position}`, token),
                );
                return res.data;
            }

            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post/view&code=${position}`, token));
            return res.data;
        },

        // Position name
        async editProductionPositionName(ctx, params) {
            const { code, name } = params;
            const token = ctx.rootState.authToken;
            const res = await axios.put(addAuthKey(`${api_url}/?r=api/post/update-name&code=${code}`, token), {
                name,
            });
            return res.data;
        },
        async editDeliveryPositionName(ctx, params) {
            const { code, name } = params;
            const token = ctx.rootState.authToken;
            const res = await axios.put(
                addAuthKey(`${api_url}/?r=api/delivery-post/update-name&id=${code}`, token),
                {
                    name,
                },
            );
            return res.data;
        },
        async editPositionName(ctx, params) {
            const { isDelivery } = params;
            return ctx.dispatch(isDelivery ? "editDeliveryPositionName" : "editProductionPositionName", params);
        },

        // Production position workload
        async editProductionPositionWorkload(ctx, params) {
            const { code, data, filial } = params;
            const token = ctx.rootState.authToken;
            const res = await axios.put(addAuthKey(`${api_url}/?r=api/post/update-workload&code=${code}`, token), {
                ...data,
                filial_id: filial,
            });
            return res.data;
        },

        // Position salary
        async editProductionPositionSalary(ctx, params) {
            const { position, data, filial } = params;
            const token = ctx.rootState.authToken;
            const res = await axios.put(addAuthKey(`${api_url}/?r=api/post/update-salary&code=${position}`, token), {
                ...data,
                filial_id: filial,
            });
            return res.data;
        },
        async editDeliveryPositionSalary(ctx, params) {
            const { position, data, filial } = params;
            const token = ctx.rootState.authToken;
            const res = await axios.put(
                addAuthKey(`${api_url}/?r=api/delivery-post/update-salary&id=${position}`, token),
                {
                    ...data,
                    filial_id: filial,
                },
            );
            return res.data;
        },

        async getPositionsList({ rootState, commit }) {
            const token = rootState.authToken;
            const url = addAuthKey(`${api_url}?r=api/directory/position`, token);

            const { data } = await axios.get(url);

            const positions = Object.entries(data).map(([positionCode, { name }]) => ({
                label: name,
                value: positionCode,
            }));

            commit("setPositionsList", positions);

            return positions;
        },

        async getPositionsDriversList({ rootState }) {
            const token = rootState.authToken;
            const url = addAuthKey(`${api_url}?r=api/directory/position-drivers`, token);

            const { data } = await axios.get(url);

            const positions = Object.entries(data).map(([positionCode, { name }]) => ({
                label: name,
                value: positionCode,
            }));

            return positions;
        },

        async getPositionsInSortingOrder({ rootState }) {
            const token = rootState.authToken;
            const url = addAuthKey(`${api_url}?r=api/directory/position`, token);

            const { data } = await axios.get(url);

            const positions = Object.entries(data).map(([positionCode, { name, categories }]) => {
                let ranks = categories;

                if (Object.keys(ranks).length) {
                    ranks = Object.entries(ranks).map(([rankId, rank]) => ({
                        label: rank,
                        value: rankId,
                    }));
                }

                return {
                    label: name,
                    value: positionCode,
                    ranks,
                };
            });

            return positions;
        },

        async getPositionRanks(ctx, params) {
            const token = ctx.rootState.authToken;
            let p, f;
            if (typeof params=="object") {
                const {positionCode, filial} = params;
                p=positionCode; f=filial;
            } else p = params
            let url = addAuthKey(`${api_url}/?r=api/post/index-rank&code=${p}`, token);
            if (f) url += `&filial_id=${f}`;
            const res = await axios.get(url);
            return res.data;
        },
        async getRank(ctx, params) {
            const token = ctx.rootState.authToken;
            let p, f;
            if (typeof params=="object") {
                const {rankId, filial} = params;
                p=rankId; f=filial;
            } else p = params
            let url = addAuthKey(`${api_url}/?r=api/post/view-rank&id=${p}`, token);
            if (f) url += `&filial_id=${f}`;
            const res = await axios.get(url);
            return res.data;
        },
        async removeRank(ctx, rankId) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post/delete-rank&id=${rankId}`, token));
            return res.data;
        },
        async saveRank(ctx, params) {
            const token = ctx.rootState.authToken;
            const { id, filial_id, ...rankParams } = params;

            let url = addAuthKey(`${api_url}/?r=api/post/save-rank`, token);
            if (id) url += `&id=${id}`;
            if (filial_id) url += `&filial_id=${filial_id}`;
            const res = await axios.post(url, rankParams);
            return res.data;
        },

        async getPositionSpecialties(ctx, positionCode) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(
                addAuthKey(`${api_url}/?r=api/post-specialty/index&code=${positionCode}`, token),
            );
            return res.data;
        },
        async removeSpecialty(ctx, specialtyId) {
            const token = ctx.rootState.authToken;
            const res = await axios.delete(
                addAuthKey(`${api_url}/?r=api/post-specialty/delete&id=${specialtyId}`, token),
            );
            return res.data;
        },
        async addSpecialty(ctx, params) {
            const token = ctx.rootState.authToken;
            const { name, position } = params;

            const res = await axios.post(addAuthKey(`${api_url}/?r=api/post-specialty/create`, token), {
                name,
                post_code: position,
            });
            return res.data;
        },
        async editSpecialty(ctx, params) {
            const token = ctx.rootState.authToken;
            const { id, name } = params;

            const res = await axios.post(addAuthKey(`${api_url}/?r=api/post-specialty/update&id=${id}`, token), {
                name,
            });
            return res.data;
        },

        async editProductionPositionShifts(ctx, params) {
            const { position, shifts } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.post(addAuthKey(`${api_url}/?r=api/post/save-shift`, token), {
                post_code: position,
                shifts_data: shifts,
            });

            return res.data;
        },

        async editDeliveryPositionShifts(ctx, params) {
            const { position, shifts } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.post(
                addAuthKey(`${api_url}/?r=api/delivery-post/update-shift&id=${position}`, token),
                {
                    timesettings: shifts,
                },
            );

            return res.data;
        },

        // Deposit

        async editProductionPositionDeposit(ctx, params) {
            const { position, data, filial } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.put(
                addAuthKey(`${api_url}/?r=api/post/update-deposit&code=${position}`, token),
                {
                    ...data,
                    filial_id: filial,
                },
            );

            return res.data;
        },

        async editDeliveryPositionDeposit(ctx, params) {
            const { position, data, filial } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.put(
                addAuthKey(`${api_url}/?r=api/delivery-post/update-deposit&id=${position}`, token),
                {
                    ...data,
                    filial_id: filial,
                },
            );

            return res.data;
        },

        async editDeposit(ctx, params) {
            const { isDelivery } = params;
            return ctx.dispatch(isDelivery ? "editDeliveryPositionDeposit" : "editProductionPositionDeposit", params);
        },

        async getPositionValues(ctx, positionCode) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post-uniform/view&id=${positionCode}`, token));
            return res.data;
        },

        async editMaterialValues(ctx, params) {
            const { position, values } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.put(addAuthKey(`${api_url}/?r=api/post-uniform/save&id=${position}`, token), {
                tmc: values,
            });

            return res.data;
        },

        async getPositionSurcharges(ctx, params) {
            const token = ctx.rootState.authToken;
            let { positionCode, filial } = params;
            if  (!filial) filial='';
            const res = await axios.get(
                addAuthKey(`${api_url}/?r=api/post-surcharge/view&id=${positionCode}&filial_id=${filial}`, token),
            );
            return res.data;
        },

        async editProductionPositionSurcharges(ctx, params) {
            let { position, surcharges, filial } = params;
            const token = ctx.rootState.authToken;
            if  (!filial) filial='';
            const res = await axios.put(addAuthKey(`${api_url}/?r=api/post-surcharge/save&id=${position}&filial_id=${filial}`, token), {
                surcharge_data: surcharges,
            });
            return res.data;
        },

        async getDeliveryPositionSurcharges(ctx, positionCode) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(
                addAuthKey(`${api_url}/?r=api/delivery-post-surcharge/view&id=${positionCode}`, token),
            );
            return res.data;
        },

        async editDeliveryPositionSurcharges(ctx, params) {
            const { position, surcharges } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.put(
                addAuthKey(`${api_url}/?r=api/delivery-post-surcharge/save&id=${position}`, token),
                {
                    data: surcharges,
                },
            );
            return res.data;
        },

        async editDeliveryPositionBonuses(ctx, params) {
            const { position, data } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.put(
                addAuthKey(`${api_url}/?r=api/delivery-post/update-surcharge&id=${position}`, token),
                data,
            );

            return res.data;
        },

        async getPositionRecruitment(ctx, positionCode) {
            const token = ctx.rootState.authToken;
            const res = await axios.get(
                addAuthKey(`${api_url}/?r=api/post-recruiting/view&id=${positionCode}`, token),
            );
            return res.data;
        },

        async getPositionVacancies(ctx, {positionCode, filialId}) {
            const token = ctx.rootState.authToken;

            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post-recruiting/vacancies&code=${positionCode}${filialId ? '&filial_id=' + filialId : ''}`, token));

            return res.data;
        },

        async getPositionFunctions(ctx, {positionCode, filialId}) {
            const token = ctx.rootState.authToken;

            const res = await axios.get(addAuthKey(`${api_url}/?r=api/post-functions/index&code=${positionCode}${filialId ? '&filial_id=' + filialId : ''}`, token));

            return res.data;
        },

        async editRecruitment(ctx, params) {
            const { position, data } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.post(
                addAuthKey(`${api_url}/?r=api/post-recruiting/update&id=${position}`, token),
                data,
            );

            return res.data;
        },

        async getCalculations(ctx, params) {
            const { from, to, positionCode } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.get(
                addAuthKey(`${api_url}/?r=api/calculation/get-for-recalc&code=${positionCode}&from=${from}&to=${to}`, token),
            );

            return res.data;
        },

        async recalculateCalculations(ctx, params) {
            const { from, to, positionCode } = params;
            const token = ctx.rootState.authToken;

            const res = await axios.get(
                addAuthKey(`${api_url}/?r=api/calculation/recalc&code=${positionCode}&from=${from}&to=${to}`, token),
            );

            return res.data;
        },

        async getCompetencies(ctx) {
            const token = ctx.rootState.authToken;

            const res = await axios.get(
                addAuthKey(`${api_url}/?r=api/competency/index&status_type=active`, token),
            );

            return res.data;
        },

        async saveCompetencies(ctx, {params, code}) {
            const token = ctx.rootState.authToken;

            const res = await axios.post(
                addAuthKey(`${api_url}/?r=api/post-competence/save&id=${code}`, token),
                params,
            );

            return res.data;
        },

        async getPostCompetencies(ctx, code) {
            const token = ctx.rootState.authToken;

            const res = await axios.get(
                addAuthKey(`${api_url}/?r=api/post-competence/view&id=${code}`, token),
            );

            return res.data;
        },

        async movePostToArchive({rootState}, {code}) {
            const token = rootState.authToken;

            let {data} = await axios.post(
                addAuthKey(`${api_url}?r=api/posts/history`, token),
                {
                    post_codes: [code],
                    action: 'to_archive'
                }
            );

            return data;
        },
        async movePostFromArchive({rootState}, {code}) {
            const token = rootState.authToken;

            let {data} = await axios.post(
                addAuthKey(`${api_url}?r=api/posts/history`, token),
                {
                    post_codes: [code],
                    action: 'from_archive'
                }
            );

            return data;
        },
    },
};
